import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { getLenis, startLenis, stopLenis } from './lenis'

export function initLetter(): void {
  const letter = document.getElementById('letter')
  const envelope = document.getElementById('envelope')
  const envelopeStage = letter?.querySelector('.letter__envelope-stage') as HTMLElement | null
  const paper = letter?.querySelector('.letter__paper')
  const content = letter?.querySelector('.letter__content')
  const letterSection = document.getElementById('letter-section')

  if (!letter || !envelope || !paper || !content || !letterSection) return

  let isStarted = false
  let isPendingStart = false
  let hasSvgLoaded = false
  let hasPreClickLock = false
  let isPreparingAnimationStart = false
  let isScrollLockedForTap = false

  const lockScrollForTap = () => {
    if (isScrollLockedForTap || isStarted) return
    isScrollLockedForTap = true
    document.documentElement.classList.add('is-scroll-locked')
    document.body.classList.add('is-scroll-locked')
    stopLenis()
  }

  const unlockScrollForTap = () => {
    if (!isScrollLockedForTap) return
    isScrollLockedForTap = false
    document.documentElement.classList.remove('is-scroll-locked')
    document.body.classList.remove('is-scroll-locked')
    startLenis()
  }

  const resetToClosedState = () => {
    isStarted = false
    isPendingStart = false
    hasSvgLoaded = false
    hasPreClickLock = false

    letter.classList.remove('is-opened', 'is-animating', 'is-complete', 'is-ready', 'letter--settled')
    letterSection.classList.remove('letter-section--read-mode')
    envelopeStage?.classList.remove('is-hidden')
    content.classList.remove('is-visible')
    envelope.innerHTML = ''
    unlockScrollForTap()
  }

  const settleIntoReadMode = () => {
    const paperEl = paper as HTMLElement

    // Capture where the paper is right now
    const beforeRect = paperEl.getBoundingClientRect()

    // Kill CSS transition so the layout switch doesn't animate
    paperEl.style.transition = 'none'

    // Switch to read-mode layout
    letterSection.classList.add('letter-section--read-mode')
    envelopeStage?.classList.add('is-hidden')
    letter.classList.add('letter--settled')

    // Force reflow so new layout is computed
    void paperEl.offsetHeight

    // Capture where the paper ended up
    const afterRect = paperEl.getBoundingClientRect()

    // Smooth out any position difference
    const dy = beforeRect.top - afterRect.top
    if (Math.abs(dy) > 2) {
      gsap.fromTo(
        paperEl,
        { y: dy, xPercent: -50 },
        { y: 0, xPercent: -50, duration: 0.5, ease: 'power2.out', clearProps: 'transform' }
      )
    }

    // Re-enable CSS transitions after a frame
    requestAnimationFrame(() => {
      paperEl.style.transition = ''
    })

    // Paper is settled and content visible — unlock scroll so user can read
    unlockScrollForTap()
  }

  const revealPaper = () => {
    // Don't switch layout yet - keep envelope visible, letter is absolute and slides up
    letter.classList.add('is-opened')
  }

  const revealLetterContent = () => {
    content.classList.add('is-visible')
  }

  const getCenteredAnimationTop = () => {
    const stage = letter.querySelector('.letter__envelope-stage') as HTMLElement | null
    const anchor = stage ?? letter
    const rect = anchor.getBoundingClientRect()
    const targetTop = window.scrollY + rect.top - (window.innerHeight - rect.height) * 0.5
    return Math.max(0, targetTop)
  }

  const getSectionTop = () => {
    const rect = letterSection.getBoundingClientRect()
    return Math.max(0, window.scrollY + rect.top)
  }

  const createEnvelopeTimeline = () => {
    const svg = envelope.querySelector('svg')
    if (!svg) return null

    const q = gsap.utils.selector(svg)
    const timeline = gsap.timeline({
      defaults: { ease: 'power2.inOut' },
      onComplete: () => {
        letter.classList.remove('is-animating')
        letter.classList.add('is-complete')
      },
    })

    timeline
      // On click, do not replay the intro. Just hide helper UI and open.
      .to(q('#arrow'), { autoAlpha: 0, duration: 0.18 })
      .to(
        q('#button'),
        {
          autoAlpha: 0,
          scale: 0,
          duration: 0.22,
          transformOrigin: 'center center',
        },
        '<'
      )
      .to(q('#text > *'), { autoAlpha: 0, duration: 0.18, stagger: 0.03 }, '<')
      .to(q('#closed'), {
        duration: 2,
        transformOrigin: 'center top',
        fill: '#f5f5f5',
        scaleY: -1,
        ease: 'linear',
      })
      .to(
        q('#pattern-top'),
        {
          duration: 1.5,
          autoAlpha: 1,
          transformOrigin: 'center bottom',
          scaleY: 1,
          ease: 'linear',
        },
        '-=1'
      )
      .to(
        q('#paper'),
        {
          duration: 2,
          autoAlpha: 1,
          scaleY: 1,
          transformOrigin: 'center bottom',
        },
        '-=2.5'
      )
      .to(q('#paper-mask'), { y: '+=500', duration: 3.0 })
      // Start revealing the actual letter (still absolute) - it slides up behind/through the envelope
      .add(() => {
        revealPaper()
      }, '<+1.5')
      .add(() => {
        revealLetterContent()
      }, '<+0.2')
      // Wait for letter to travel most of the way up, then slide the whole envelope down
      .to(
        q('#pattern-top, #closed, #shadows-inner, #pattern-bottom, #accents, #body, #bottom-shadow, #paper'),
        {
          y: '+=900',
          duration: 1.0,
          ease: 'power3.in',
          onComplete: () => {
            // Envelope is off-screen now - switch to read-mode layout
            settleIntoReadMode()
          },
        },
        '<+1.5'
      )
      .from(q('#paper-mask-full'), { autoAlpha: 0, duration: 0.01 }, '-=0.5')
      .from(q('#shadows-inner'), { autoAlpha: 0, y: '+=2' }, 0.1)
      .set(q('#arrow, #button, #text'), { autoAlpha: 0 })
      .set(q('#paper-mask-full'), { autoAlpha: 1 })

    return timeline
  }

  const startAnimation = () => {
    if (isStarted) return

    if (!hasSvgLoaded) {
      isPendingStart = true
      return
    }

    isStarted = true
    letter.classList.add('is-animating')

    const timeline = createEnvelopeTimeline()
    if (!timeline) {
      settleIntoReadMode()
      revealPaper()
      revealLetterContent()
      letter.classList.remove('is-animating')
      letter.classList.add('is-complete')
      return
    }

    timeline.play(0)
  }

  const loadEnvelopeSvg = async () => {
    try {
      const response = await fetch('/envelope.svg')
      if (!response.ok) throw new Error(`Failed to fetch envelope.svg: ${response.status}`)

      const svgText = await response.text()
      envelope.innerHTML = svgText

      const q = gsap.utils.selector(envelope)
      gsap.set(q('#pattern-top'), {
        autoAlpha: 0,
        scaleY: 0,
        transformOrigin: 'center bottom',
      })
      gsap.set(q('#paper-mask-full'), {
        autoAlpha: 0,
      })
      gsap.set(q('#closed'), {
        autoAlpha: 1,
        scaleY: 1,
        transformOrigin: 'center top',
      })
      gsap.set(q('#paper'), {
        autoAlpha: 0,
        scaleY: 0,
        transformOrigin: 'center bottom',
      })

      hasSvgLoaded = true
      letter.classList.add('is-ready')
      if (isPendingStart && !isStarted) {
        isPendingStart = false
        startAnimation()
      }
    } catch (error) {
      console.error('Unable to load envelope SVG', error)
      hasSvgLoaded = false
      if (isPendingStart && !isStarted) {
        isPendingStart = false
        isStarted = true
        settleIntoReadMode()
        revealPaper()
        revealLetterContent()
        letter.classList.add('is-complete')
      }
    }
  }

  const handleActivate = (event: Event) => {
    event.preventDefault()

    if (isStarted || isPreparingAnimationStart) return
    isPreparingAnimationStart = true

    const nextTop = getCenteredAnimationTop()
    const delta = Math.abs(window.scrollY - nextTop)
    const runAnimation = () => {
      isPreparingAnimationStart = false
      startAnimation()
    }

    // If we already locked for this section, start immediately on click.
    if (isScrollLockedForTap) {
      runAnimation()
      return
    }

    if (delta < 10) {
      runAnimation()
      return
    }

    const lenis = getLenis()
    if (lenis) {
      lenis.scrollTo(nextTop, {
        duration: 0.75,
        easing: (t: number) => 1 - Math.pow(1 - t, 3),
      })
      window.setTimeout(() => {
        runAnimation()
      }, 760)
      return
    }

    window.scrollTo({ top: nextTop, behavior: 'smooth' })
    window.setTimeout(() => {
      runAnimation()
    }, 700)
  }

  const engagePreClickLock = () => {
    if (hasPreClickLock || isStarted || isPreparingAnimationStart) return
    if (letterSection.classList.contains('letter-section--read-mode')) return
    hasPreClickLock = true

    const nextTop = getSectionTop()
    // Constellation-style: lock first, then snap into exact section position.
    lockScrollForTap()
    window.scrollTo({ top: nextTop, behavior: 'auto' })
  }

  letter.setAttribute('tabindex', '0')
  letter.setAttribute('role', 'button')

  resetToClosedState()

  paper.classList.remove('is-open')
  gsap.set(paper as HTMLElement, {
    height: 'auto',
  })

  void loadEnvelopeSvg()

  ScrollTrigger.create({
    trigger: letterSection,
    start: 'top top',
    end: 'bottom top',
    onEnter: () => {
      engagePreClickLock()
    },
    onEnterBack: () => {
      engagePreClickLock()
    },
  })

  letter.addEventListener('click', handleActivate)
  letter.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    startAnimation()
  })
}
